import { cn } from "@genuin/ui/lib/utils";
import type { EmbedTileProps } from "./embed-tile.types";
import { cva, VariantProps } from "class-variance-authority";
import {
  ControlLayer,
  FeedPlayer,
} from "@genuin/components/molecules/feed-player";
import {
  PlayerProvider,
  usePlayerContext,
} from "@genuin/components/molecules/feed-player/context";
import { useBoolean } from "usehooks-ts";
import { useEmbedConfigs } from "@genuin/components/hooks/embed/use-embed-config";
import { Linkouts } from "../linkouts";
import { Stats } from "@genuin/components/molecules/stats";
import { CommentIcon, PlayIcon } from "@genuin/ui/icons";
import { PostDetailsType } from "@genuin/components/react-query/api/feed/schema";
import { useCallback, useMemo } from "react";
import { useEmbedContext } from "@genuin/components/context/embed";
import { DynamicReactionIcon } from "@genuin/components/molecules/reaction-button";

/**
 * Helper function to extract the most appropriate URL from linkouts based on priority:
 * 1. CTA link from first linkout
 * 2. First link from links array in first linkout
 * 3. URL from first linkout (legacy format)
 * 4. null if no valid URL found
 */
function getLinkoutUrl(linkouts: any): string | null {
  if (!linkouts || typeof linkouts !== "object") {
    return null;
  }

  // Handle array of linkout objects
  if (Array.isArray(linkouts) && linkouts.length > 0) {
    const firstLinkout = linkouts[0];

    // First priority: CTA link
    if (firstLinkout.cta_link) {
      return firstLinkout.cta_link;
    }

    // Second priority: First link from links array
    if (
      firstLinkout.links &&
      Array.isArray(firstLinkout.links) &&
      firstLinkout.links.length > 0 &&
      firstLinkout.links[0].link
    ) {
      return firstLinkout.links[0].link;
    }

    // Legacy format with url property
    if (firstLinkout.url) {
      return firstLinkout.url;
    }
  }

  // Handle direct object with cta_link
  if (!Array.isArray(linkouts) && linkouts.cta_link) {
    return linkouts.cta_link;
  }

  return null;
}

const embedTileVariants = cva(
  "gencl:h-full gencl:rounded-lg gencl:overflow-clip gencl:flex gencl:flex-col",
  {
    variants: {
      variant: {
        default: "gencl:bg-white",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export function EmbedTile({
  isActive,
  variant,
  embedType = 0,
  postDetails,
  className,
  index,
  onPlayerIterationEnd,
  ...restProps
}: EmbedTileProps & VariantProps<typeof embedTileVariants>) {
  const { value, toggle } = useBoolean(false);
  // Use the structured config object
  const config = useEmbedConfigs();

  return (
    <div
      className={cn(
        embedTileVariants({ variant }),
        {
          "gencl:border gencl:border-secondary-150":
            config.video.showBorderAroundVideo,
        },
        className
      )}
      {...restProps}
    >
      {/* Take available height after showLinkOutside & showSocialInteractionData gets its height   */}
      <PlayerProvider
        isActive={isActive}
        index={index}
        videoId={postDetails.video.id}
        showExpandView={value}
        toggleExpandView={toggle}
        onPlayerIterationEnd={onPlayerIterationEnd}
        isEmbed
        explicitAutoPlay={config.video.videoAutoplay && isActive}
        explicitLoop={config.video.videoLoop && isActive}
      >
        <EmbedPlayer
          postDetails={postDetails}
          isActive={isActive}
          index={index}
        />
      </PlayerProvider>
      {config.links.showLinkOutside &&
        config.engagement.showEngagementOnRootElement && (
          <div className="gencl:h-27 gencl:w-full gencl:flex gencl:items-center">
            <Linkouts
              variant="embed"
              isActive={true}
              isOutside
              showImmediately
              linkouts={postDetails.video.linkouts}
              linkoutId={postDetails.video.linkoutId}
            />
          </div>
        )}

      {config.links.showLinkOutside &&
        config.engagement.showSocialInteractionData &&
        config.engagement.showEngagementOnRootElement && (
          <hr className="gencl:w-[90%] gencl:border-secondary-150 gencl:mx-auto" />
        )}

      {config.engagement.showSocialInteractionData &&
        config.engagement.showEngagementOnRootElement && (
          <div className="gencl:h-10">
            <Stats
              className={cn(
                "gencl:flex gencl:gap-2 gencl:justify-between gencl:p-3 gencl:w-full"
              )}
              valueClassName="gencl:text-black! gencl:text-body-2-medium"
              stats={{
                Views: {
                  value: 0,
                  icon: <PlayIcon theme="light" size="sm" />,
                },
                Reactions: {
                  value: postDetails.video.sparkCount,
                  icon: (
                    <DynamicReactionIcon
                      sparkCount={0}
                      isSparked={false}
                      iconHeight={16}
                      iconWidth={16}
                      theme="light"
                    />
                  ),
                },
                Comments: {
                  value: postDetails.video.commentCount,
                  icon: <CommentIcon theme="light" size="sm" />,
                },
              }}
            />
          </div>
        )}
    </div>
  );
}

type EmbedPlayerProps = {
  postDetails: PostDetailsType;
  isActive?: boolean;
  index: number;
};

function EmbedPlayer({
  postDetails,
  isActive = false,
  index,
}: EmbedPlayerProps) {
  const { isAdPlaying } = usePlayerContext();
  const config = useEmbedConfigs();
  const { changeActivePlayerType, embedData, rootElement } = useEmbedContext();

  const handleClickOnEmbedTile = useCallback(() => {
    if (isAdPlaying) {
      return;
    }

    if (embedData.card_layout_id === 4) {
      // For card_layout_id 4, prioritize URLs in this order: CTA, first linkout, or no action
      const linkoutUrl = getLinkoutUrl(postDetails.video.linkouts);

      if (linkoutUrl) {
        window.open(linkoutUrl, "_blank");
        return;
      }

      // No expand view for card_layout_id 4(Grubhub)
      return;
    }

    // Default behavior for other card layouts
    if (postDetails.video.clickableUrl) {
      window.open(postDetails.video.clickableUrl, "_blank");
      return;
    }

    if (config.expandViewConfig.enable) {
      changeActivePlayerType("expand-view", index);
    }
  }, [
    isAdPlaying,
    changeActivePlayerType,
    index,
    postDetails,
    embedData.card_layout_id,
  ]);

  // Determine sizing based on rootElement dimensions
  // const adaptiveSizing = useMemo(() => {
  //   if (!rootElement) return "gencl:h-full"; // fallback

  //   const containerHeight = rootElement.clientHeight;
  //   const containerWidth = rootElement.clientWidth;

  //   // If container is wider than tall (landscape), use h-full to fit height
  //   return containerHeight > containerWidth ? "gencl:w-full" : "gencl:h-full";
  // }, [rootElement]);

  return (
    <div
      className={cn(
        "gencl:relative gencl:bg-black gencl:flex-1 gencl:min-h-0 gencl:flex gencl:items-center gencl:justify-center",
        {
          "gencl:opacity-50 gencl:transition-opacity":
            !isActive && config.styling.isOpacityDown,
        }
      )}
    >
      <FeedPlayer
        videoId={postDetails.video.id}
        adUrl={postDetails.video.adUrl ?? undefined}
        src={postDetails.video.source}
        poster={postDetails.video.thumbnail}
        className={
          !config.video.videoCrop
            ? "gencl:h-full"
            : "gencl:object-cover gencl:h-full gencl:w-full"
        }
      />
      <ControlLayer
        variant={config.view.isPlacementView ? "placement" : "embed"}
        isActive={isActive}
        postDetails={postDetails}
        onClick={handleClickOnEmbedTile}
        onCommentCountChange={undefined}
      />
    </div>
  );
}
